import { z } from "zod";

/**
 * Job Match: how a resume reads against one job, requirement by requirement.
 *
 * The server writes the checks from the job posting (at most three must-haves), then judges each
 * one against numbered lines of the resume. A requirement counts as shown only when a line proves
 * it; the quote shown to the candidate is that stored line, so evidence can't be invented.
 */

export const JOB_MATCH_VERSION = 1;
export const JOB_MATCH_MAX_MUST_HAVES = 3;

/** Where a resume line came from, so the client can quote it, jump to it and edit it. */
export const matchLineRefSchema = z.object({
  section: z.string(),
  itemId: z.string().nullable().default(null),
  /**
   * summary: a bullet or paragraph of the item's description; keywords: a skill group; content:
   * the whole professional summary (a job-specific rewrite replaces it rather than adding a line).
   */
  field: z.enum(["headline", "summary", "header", "keywords", "name", "content"]),
  /** Which bullet or paragraph within `field` (summary only). */
  index: z.number().int().nullable().default(null),
});
export type MatchLineRef = z.infer<typeof matchLineRefSchema>;

export const matchLineSchema = z.object({ ref: matchLineRefSchema, text: z.string() });
export type MatchLine = z.infer<typeof matchLineSchema>;

export const matchCheckSchema = z.object({
  id: z.string(),
  /** The requirement as a recruiter would write it. */
  text: z.string(),
  /** The short term people search for ("TypeScript", "CI/CD"); null when there isn't one. */
  term: z.string().nullable(),
  weight: z.enum(["must_have", "nice_to_have"]),
  definition: z.string(),
  /** years: total years of work; role_years: years doing `work`; ai: judged from the lines. */
  kind: z.enum(["ai", "years", "role_years"]),
  years: z.number().nullable(),
  work: z.string().nullable(),
});
export type MatchCheck = z.infer<typeof matchCheckSchema>;

export const MATCH_STATUSES = ["shown", "close", "missing"] as const;
export type MatchStatus = (typeof MATCH_STATUSES)[number];

export const matchResultSchema = z.object({
  checkId: z.string(),
  status: z.enum(MATCH_STATUSES),
  /** Lines that prove it (shown) or come near it (close), best first. */
  evidence: z.array(matchLineSchema),
  /** The line a rewording would start from: the best related line, even under "missing". */
  related: matchLineSchema.nullable(),
  /** Years checks: the years the dates add up to. */
  measured: z.number().nullable().default(null),
});
export type MatchResult = z.infer<typeof matchResultSchema>;

export const matchSummarySchema = z.object({
  mustShown: z.number().int(),
  mustTotal: z.number().int(),
  otherShown: z.number().int(),
  otherTotal: z.number().int(),
});
export type MatchSummary = z.infer<typeof matchSummarySchema>;

export const jobMatchSchema = z.object({
  version: z.number().int(),
  /** Hash of the job (title, company, description) the checks were written from. */
  jobHash: z.string(),
  jobTitle: z.string(),
  jobCompanyName: z.string(),
  checks: z.array(matchCheckSchema),
  /** The posting doesn't say what the job requires; `whatToAdd` says what's missing. */
  vague: z.boolean(),
  whatToAdd: z.string().nullable(),
  /** Null until the first score lands. */
  results: z.array(matchResultSchema).nullable(),
  /** Checks the candidate said they don't have: not suggested again for this job. */
  gaps: z.array(z.string()),
  linesHash: z.string().nullable(),
  checkedAt: z.string().nullable(),
  summary: matchSummarySchema.nullable(),
});
export type JobMatch = z.infer<typeof jobMatchSchema>;

/** Body of POST /resume/:id/match/job. */
export const matchJobInputSchema = z.object({
  jobTitle: z.string().max(200).default(""),
  jobCompanyName: z.string().max(200).default(""),
  jobDescription: z.string().min(40).max(30_000),
});
export type MatchJobInput = z.infer<typeof matchJobInputSchema>;

/** A rewording of one line, or a new bullet written from the candidate's answer. */
export const matchDraftSchema = z.object({
  checkId: z.string(),
  target: matchLineRefSchema,
  before: z.string(),
  /** Plain text. Blanks for facts only the candidate knows are written [[1]], [[2]]. */
  after: z.string(),
  /** One question per blank, in order. */
  gaps: z.array(z.object({ ask: z.string() })),
  why: z.string(),
});
export type MatchDraft = z.infer<typeof matchDraftSchema>;

/** A headline and summary written for the target job. Either is null when it couldn't be
 * written from the resume without adding facts. */
export const matchIntroSchema = z.object({
  headline: matchDraftSchema.nullable(),
  summary: matchDraftSchema.nullable(),
});
export type MatchIntro = z.infer<typeof matchIntroSchema>;

/** Body of POST /resume/:id/match/copy: the job a new, tailored copy of the resume is for. */
export const matchCopyResultSchema = z.object({ resumeId: z.string(), title: z.string() });
export type MatchCopyResult = z.infer<typeof matchCopyResultSchema>;

/** Body of POST /resume/:id/match/:checkId/answer. */
export const matchAnswerInputSchema = z.object({
  section: z.string(),
  itemId: z.string(),
  answer: z.string().min(3).max(600),
});
export type MatchAnswerInput = z.infer<typeof matchAnswerInputSchema>;

/** Counts behind "2 of 3 must-haves shown". Confirmed gaps still count as not shown. */
export function summariseMatch(checks: MatchCheck[], results: MatchResult[]): MatchSummary {
  const status = new Map(results.map((r) => [r.checkId, r.status]));
  const musts = checks.filter((c) => c.weight === "must_have");
  const others = checks.filter((c) => c.weight !== "must_have");
  return {
    mustShown: musts.filter((c) => status.get(c.id) === "shown").length,
    mustTotal: musts.length,
    otherShown: others.filter((c) => status.get(c.id) === "shown").length,
    otherTotal: others.length,
  };
}

// ---------------------------------------------------------------------------
// Rich-text segments
//
// Descriptions are stored as simple HTML: a <ul> of <li><p>…</p></li>, or <p> paragraphs. One
// segment is one bullet (or one paragraph when there are no bullets). Both the server (building
// lines) and the client (applying an edit) use these, so a line's index always means the same
// bullet. Regex-based on purpose: the server has no DOM, and the editor's HTML is this simple.
// ---------------------------------------------------------------------------

const LI = /<li\b[^>]*>([\s\S]*?)<\/li>/gi;
const P = /<p\b[^>]*>([\s\S]*?)<\/p>/gi;

const ENTITIES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
  "&nbsp;": " ",
};

/** Plain text of an HTML fragment, whitespace collapsed. */
export function htmlToText(html: string): string {
  return (html || "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&(?:amp|lt|gt|quot|#39|apos|nbsp);/g, (m) => ENTITIES[m] ?? m)
    .replace(/\s+/g, " ")
    .trim();
}

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** The bullets (or paragraphs) of a description, as plain text. Empty ones are kept as "". */
export function htmlSegments(html: string): string[] {
  const source = html || "";
  const items = [...source.matchAll(LI)].map((m) => htmlToText(m[1]));
  if (items.length > 0) return items;
  const paragraphs = [...source.matchAll(P)].map((m) => htmlToText(m[1]));
  if (paragraphs.length > 0) return paragraphs;
  const text = htmlToText(source);
  return text ? [text] : [];
}

/** Replaces bullet (or paragraph) `index` with plain `text`; other bullets keep their markup. */
export function replaceHtmlSegment(html: string, index: number, text: string): string {
  const source = html || "";
  const safe = escapeHtml(text);
  const pattern = new RegExp((source.match(LI) ? LI : P).source, "gi");
  if (!source.match(LI) && !source.match(P)) return index === 0 ? `<p>${safe}</p>` : source;
  let i = 0;
  return source.replace(pattern, (whole) => {
    const at = i++;
    if (at !== index) return whole;
    return whole.startsWith("<li") || whole.startsWith("<LI")
      ? whole.replace(/^(<li\b[^>]*>)[\s\S]*(<\/li>)$/i, `$1<p>${safe}</p>$2`)
      : whole.replace(/^(<p\b[^>]*>)[\s\S]*(<\/p>)$/i, `$1${safe}$2`);
  });
}

/** Adds a bullet at the end: into the last list when there is one, else as a new list. */
export function appendHtmlBullet(html: string, text: string): string {
  const source = (html || "").trim();
  const bullet = `<li><p>${escapeHtml(text)}</p></li>`;
  const lastClose = source.toLowerCase().lastIndexOf("</ul>");
  if (lastClose >= 0) return `${source.slice(0, lastClose)}${bullet}${source.slice(lastClose)}`;
  return `${source}<ul>${bullet}</ul>`;
}

/** Splits text into plain parts and [[n]] blanks, for rendering blanks as inputs. */
export function splitBlanks(text: string): Array<{ text: string } | { blank: number }> {
  const parts: Array<{ text: string } | { blank: number }> = [];
  let last = 0;
  for (const m of text.matchAll(/\[\[(\d+)\]\]/g)) {
    if (m.index! > last) parts.push({ text: text.slice(last, m.index) });
    parts.push({ blank: Number(m[1]) });
    last = m.index! + m[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
}

/** Fills [[n]] blanks with the candidate's answers (1-based); a blank with no answer stays. */
export function fillBlanks(text: string, answers: Record<number, string>): string {
  return text.replace(/\[\[(\d+)\]\]/g, (whole, n: string) => {
    const value = answers[Number(n)]?.trim();
    return value ? value : whole;
  });
}

export const hasBlanks = (text: string) => /\[\[\d+\]\]/.test(text);
